import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { collect } from 'collect.js';
import moment from 'moment';
import { ArrowRight, Loader2, Star } from 'lucide-react';
import { useSelector } from 'react-redux';
import useAuth from '../hooks/global/useAuth';
import useCashbackProgram from '../hooks/cashback/useCashbackProgram';
import useAppMethods from '../hooks/utility/useAppMethods';
import useRouter from '../hooks/navigation/useRouter';
import useMenu from '../hooks/menu/useMenu';
import PageLayout from '../components-library/layouts/PageLayout';
import PoweredByFooter from '../components-library/footers/PoweredByFooter';
import RedeemableItemsCarousel from '../components-library/cashback/RedeemableItemsCarousel';
import PageNotFound from './PageNotFound';
import { CashbackAccountOperationSource, CategoryType, SCREENS } from '../services/exports/Constants';
import { CashbackHistoryOperation, ProductInterface } from '../services/exports/Interfaces';
import { StoreInterface } from '../store/types';
import {
  useGetCashbackOperationsStatsQuery,
  useGetCashbackUpcomingExpirationQuery,
  useLazyGetCashbackOperationsQuery,
} from '../store/api-slices';
import { selectCompanyId } from '../store/slices/InitialDataSlice';
import { getCuisineEmoji } from '../utils/cuisineEmoji';

export default function RewardsScreen() {
  const { hasCashbackProgram } = useCashbackProgram();

  if (!hasCashbackProgram) {
    return <PageNotFound />;
  }

  return (
    <PageLayout className="bg-gray-background">
      <div className="min-h-screen">
        <main className="w-full">
          <Hero />
          <RedeemableItems />
          <CashbackHistory />
          <HowItWorks />
          <FAQ />
        </main>
        <PoweredByFooter />
      </div>
    </PageLayout>
  );
}

const Hero = () => {
  const { isLoggedIn } = useAuth();

  return isLoggedIn ? <MemberHero /> : <GuestHero />;
};

const GuestHero = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:RewardsScreen' });
  const { toggleLoginModal, formatCurrency } = useAppMethods();
  const { cashbackProgram, earnRate } = useCashbackProgram();
  const company = useSelector((state: StoreInterface) => state.initialData.company);

  const programName = cashbackProgram?.title ?? `${company?.name ?? ''} Rewards`.trim();

  const onSignup = () => toggleLoginModal({ openModal: true, onCloseRedirect: () => null });

  return (
    <section className="bg-hero relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 80%, rgb(var(--primary-color-rgb) / 0.2), transparent 70%)',
        }}
      />
      <div className="layout-container pt-xl md:pt-2xl pb-lg md:pb-xl relative z-10">
        <div className="flex justify-center mb-md">
          <span className={rewardsPill}>
            <Star className="w-3 h-3 fill-brand-primary text-brand-primary" />
            {programName}
          </span>
        </div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-normal text-foreground leading-[1.05] text-center mb-xs md:mb-sm">
          <Trans t={t} components={{ highlight: <span className="text-brand-primary italic" /> }}>
            guest.headline
          </Trans>
        </h1>
        <p className="text-sm md:text-base text-muted-foreground max-w-xl mx-auto text-center mb-md leading-relaxed">
          {t('guest.subheading', { count: earnRate.count, amount: formatCurrency(earnRate.amountPerUnit) })}
        </p>
        <div className="flex flex-col items-center gap-sm">
          <button type="button" onClick={onSignup} className={ctaButton}>
            {t('guest.cta')} <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-muted-foreground">
            {t('guest.already_member')}{' '}
            <button
              type="button"
              onClick={onSignup}
              className="underline cursor-pointer hover:text-foreground transition-colors"
            >
              {t('guest.sign_in')}
            </button>
          </p>
        </div>
      </div>
    </section>
  );
};

const MemberHero = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:RewardsScreen' });
  const { navigate } = useRouter();
  const { balance, cashbackProgram, formatPointsAsMoney } = useCashbackProgram();
  const companyId = useSelector(selectCompanyId);
  const { data: upcomingExpirationData } = useGetCashbackUpcomingExpirationQuery(companyId, { skip: !companyId });
  const { data: statsData } = useGetCashbackOperationsStatsQuery(companyId, { skip: !companyId });
  const lifetimePoints = statsData?.data?.lifetime_points ?? 0;

  const minSpendingLimit = cashbackProgram?.min_spending_limit ?? 0;
  const maxSpendingLimit = cashbackProgram?.max_spending_limit ?? 0;

  const balanceValue = formatPointsAsMoney(balance);
  const maxRewardValue = maxSpendingLimit > 0 ? formatPointsAsMoney(maxSpendingLimit) : null;
  const progress = minSpendingLimit > 0 ? Math.min((balance / minSpendingLimit) * 100, 100) : 100;
  const canRedeem = balance >= minSpendingLimit && minSpendingLimit > 0;

  const upcomingExpiration = useMemo(() => {
    const upcoming = upcomingExpirationData?.data;

    if (!upcoming?.expires_at || !upcoming?.points) {
      return null;
    }

    const daysUntil = moment(upcoming.expires_at).diff(moment(), 'days');

    if (daysUntil > 30 || daysUntil < 0) {
      return null;
    }

    return { value: upcoming.points, days: daysUntil };
  }, [upcomingExpirationData]);

  return (
    <section className="bg-hero">
      <div className="layout-container py-lg md:py-2xl">
        <div className="max-w-lg mx-auto text-center">
          <p className="text-sm text-muted-foreground font-medium uppercase tracking-wider mb-2xs">
            {t('member.balance_label')}
          </p>
          <p className="text-5xl md:text-6xl text-brand-primary leading-none tabular-nums mb-2xs">{balance}</p>
          <p className="text-base text-muted-foreground">
            <Trans
              t={t}
              values={{ amount: balanceValue }}
              components={{ highlight: <span className="text-foreground font-semibold" /> }}
            >
              member.balance_suffix
            </Trans>
          </p>
          {upcomingExpiration && (
            <p className="mt-2xs text-xs text-muted-foreground">
              {t('member.expiration', { points: upcomingExpiration.value, count: upcomingExpiration.days })}
            </p>
          )}

          {lifetimePoints > 0 && (
            <div className="flex items-center justify-center gap-sm mt-md">
              <div className={lifetimeStatCard}>
                <p className="text-lg text-brand-primary tabular-nums">{lifetimePoints}</p>
                <p className="text-[10px] text-muted-foreground mt-[2px]">{t('member.pts_lifetime')}</p>
              </div>
              <div className={lifetimeStatCard}>
                <p className="text-lg text-brand-primary tabular-nums">{formatPointsAsMoney(lifetimePoints)}</p>
                <p className="text-[10px] text-muted-foreground mt-[2px]">{t('member.saved_lifetime')}</p>
              </div>
            </div>
          )}

          {minSpendingLimit > 0 && (
            <div className="max-w-[400px] mx-auto mt-md">
              <div className="flex items-center justify-between text-xs mb-2xs">
                <span className="text-muted-foreground">
                  <Trans
                    t={t}
                    values={{ points: minSpendingLimit }}
                    components={{ highlight: <span className="text-foreground font-semibold" /> }}
                  >
                    member.next_reward
                  </Trans>
                </span>
                <span className="text-brand-primary font-bold tabular-nums">
                  {balance} / {minSpendingLimit}
                </span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-primary rounded-full transition-all duration-700"
                  style={{ width: `${progress}%` }}
                />
              </div>
              {canRedeem && maxRewardValue && (
                <p className="text-[11px] text-success mt-2xs font-medium">
                  {t('member.redeem_hint', { points: maxSpendingLimit, amount: maxRewardValue })}
                </p>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={() => navigate({ web: { to: SCREENS.HOME_SCREEN } })}
            className={classnames(ctaButton, 'mt-md')}
          >
            {t('member.cta')} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

const RedeemableItems = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:RewardsScreen' });
  const { isLoggedIn } = useAuth();
  const { balance, cashbackProgram, formatPointsAsMoney, pointsToMoney } = useCashbackProgram();
  const { allCategories } = useMenu();

  const maxSpendingLimit = cashbackProgram?.max_spending_limit ?? 0;
  const maxDiscount = pointsToMoney(maxSpendingLimit);

  const items = useMemo<ProductInterface[]>(() => {
    const mostPopular = collect(allCategories ?? [])
      .where('type', CategoryType.MostPopular)
      .first() as { products?: ProductInterface[] } | undefined;

    if (!mostPopular?.products) {
      return [];
    }

    return collect(mostPopular.products).sortBy('base_price').take(8).toArray() as ProductInterface[];
  }, [allCategories]);

  if (items.length === 0 || maxSpendingLimit <= 0) {
    return null;
  }

  return (
    <section className="layout-container py-lg md:py-2xl">
      <div className="mb-md text-center">
        <p className="text-[10px] uppercase tracking-[0.2em] text-brand-primary font-bold mb-2xs">
          {isLoggedIn
            ? t('member.redeemable_eyebrow', { points: balance, amount: formatPointsAsMoney(balance) })
            : t('guest.redeemable_eyebrow')}
        </p>
        <h2 className="text-xl md:text-2xl font-normal text-foreground">
          {isLoggedIn ? t('member.redeemable_heading') : t('guest.redeemable_heading')}
        </h2>
        {isLoggedIn && (
          <p className="text-xs md:text-sm text-muted-foreground mt-2xs max-w-lg mx-auto">
            {t('member.redeemable_description', {
              maxPoints: maxSpendingLimit,
              maxAmount: formatPointsAsMoney(maxSpendingLimit),
            })}
          </p>
        )}
      </div>

      <RedeemableItemsCarousel items={items} maxDiscount={maxDiscount} />
    </section>
  );
};

const HowItWorks = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:RewardsScreen' });
  const { isLoggedIn } = useAuth();
  const { cashbackProgram, formatPointsAsMoney, earnRate } = useCashbackProgram();
  const { formatCurrency } = useAppMethods();
  const company = useSelector((state: StoreInterface) => state.initialData.company);
  const cuisineEmoji = getCuisineEmoji(company?.cuisine_types);

  const minSpendingLimit = cashbackProgram?.min_spending_limit ?? 0;
  const maxSpendingLimit = cashbackProgram?.max_spending_limit ?? 0;
  const maxAmount = maxSpendingLimit > 0 ? formatPointsAsMoney(maxSpendingLimit) : '';
  const lifetime = cashbackProgram?.point_lifetime ?? 0;
  const earnAmount = formatCurrency(earnRate.amountPerUnit);

  const stepKeys = isLoggedIn
    ? (['order', 'build', 'save', 'repeat'] as const)
    : (['sign_up', 'order', 'build', 'save'] as const);

  return (
    <section className="layout-container py-lg md:py-2xl">
      <div className="mb-md">
        <p className="text-[10px] uppercase tracking-[0.2em] text-brand-primary font-bold mb-2xs">
          {isLoggedIn ? t('how_it_works.eyebrow_member') : t('how_it_works.eyebrow_guest')}
        </p>
        <h2 className="text-xl md:text-2xl font-normal text-foreground">{t('how_it_works.title')}</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-sm">
        {stepKeys.map((key, index) => {
          const emoji = key === 'order' ? cuisineEmoji : t(`how_it_works.steps.${key}.emoji`);

          return (
            <div key={key} className={stepCard}>
              <div className={stepBadge}>{index + 1}</div>
              {cuisineEmoji && <span className="text-2xl block mb-sm mt-2xs">{emoji}</span>}
              <p className="text-sm font-bold text-foreground mb-2xs">{t(`how_it_works.steps.${key}.title`)}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                {t(`how_it_works.steps.${key}.desc`, {
                  minPoints: minSpendingLimit,
                  maxAmount,
                  lifetime,
                  count: earnRate.count,
                  amount: earnAmount,
                })}
              </p>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const FAQ = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:RewardsScreen' });
  const { cashbackProgram, formatPointsAsMoney, earnRate } = useCashbackProgram();
  const { formatCurrency } = useAppMethods();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const minPoints = cashbackProgram?.min_spending_limit ?? 0;
  const maxPoints = cashbackProgram?.max_spending_limit ?? 0;
  const maxAmount = maxPoints > 0 ? formatPointsAsMoney(maxPoints) : '';
  const lifetime = cashbackProgram?.point_lifetime ?? 0;

  const getAnswerParams = (key: string) => {
    if (key === 'earn') {
      return { count: earnRate.count, amount: formatCurrency(earnRate.amountPerUnit) };
    }

    return { minPoints, maxPoints, maxAmount, lifetime };
  };

  const keys = ['earn', 'redeem', 'max', 'validity'] as const;

  return (
    <section className="layout-container py-lg md:py-2xl">
      <div className="mb-md">
        <p className="text-[10px] uppercase tracking-[0.2em] text-brand-primary font-bold mb-2xs">{t('faq.eyebrow')}</p>
        <h2 className="text-xl md:text-2xl font-normal text-foreground">{t('faq.title')}</h2>
      </div>
      <div className="rounded-2xl border-[1.5px] border-brand-border bg-brand-card overflow-hidden divide-y divide-brand-border">
        {keys.map((key, index) => {
          const isOpen = openIndex === index;

          return (
            <button
              key={key}
              type="button"
              onClick={() => setOpenIndex(isOpen ? null : index)}
              className="w-full text-left px-md py-md hover:bg-muted/30 rounded-none transition-colors"
            >
              <div className="flex items-center justify-between gap-md">
                <p className="text-sm font-semibold text-foreground">{t(`faq.items.${key}.q`)}</p>
                <span
                  className={classnames(
                    'text-muted-foreground transition-transform duration-200 shrink-0',
                    isOpen && 'rotate-45',
                  )}
                >
                  +
                </span>
              </div>
              {isOpen && (
                <p className="text-sm text-muted-foreground leading-relaxed mt-sm pr-xl">
                  {t(`faq.items.${key}.a`, getAnswerParams(key))}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </section>
  );
};

const FIRST_PAGE_SIZE = 5;
const LOAD_MORE_PAGE_SIZE = 10;

const SOURCE_PRESENTATION: Record<
  CashbackAccountOperationSource,
  { icon: string; sign: 'positive' | 'negative' | 'redeem' }
> = {
  [CashbackAccountOperationSource.Cashback]: { icon: '💰', sign: 'positive' },
  [CashbackAccountOperationSource.GoogleReviewReward]: { icon: '⭐', sign: 'positive' },
  [CashbackAccountOperationSource.WelcomeBonus]: { icon: '🎉', sign: 'positive' },
  [CashbackAccountOperationSource.MobileAppSignupBonus]: { icon: '📱', sign: 'positive' },
  [CashbackAccountOperationSource.RewardLoyaltyProgramConversion]: { icon: '🔄', sign: 'positive' },
  [CashbackAccountOperationSource.CashbackUsageRefund]: { icon: '↩️', sign: 'positive' },
  [CashbackAccountOperationSource.CashbackUsage]: { icon: '🎁', sign: 'redeem' },
  [CashbackAccountOperationSource.Refund]: { icon: '↪️', sign: 'negative' },
  [CashbackAccountOperationSource.ExpiredPointsVoid]: { icon: '⏳', sign: 'negative' },
};

const CashbackHistory = () => {
  const { t } = useTranslation(null, { keyPrefix: 'Pages:RewardsScreen' });
  const { isLoggedIn } = useAuth();
  const { hasCashbackProgram } = useCashbackProgram();
  const { formatCurrency } = useAppMethods();
  const companyId = useSelector(selectCompanyId);
  const [trigger, { isFetching }] = useLazyGetCashbackOperationsQuery();
  const [items, setItems] = useState<CashbackHistoryOperation[]>([]);
  const [meta, setMeta] = useState<{ current_page: number; last_page: number } | null>(null);

  useEffect(() => {
    if (!companyId || !isLoggedIn || !hasCashbackProgram) {
      return;
    }

    let cancelled = false;
    setItems([]);
    setMeta(null);
    trigger({ companyId, page: 1, pageSize: FIRST_PAGE_SIZE })
      .unwrap()
      .then((res) => {
        if (cancelled) return;
        setItems(res.data ?? []);
        setMeta(res.meta ?? null);
      })
      .catch(() => null);

    return () => {
      cancelled = true;
    };
  }, [companyId, isLoggedIn, hasCashbackProgram]);

  const hasMore = !!meta && meta.current_page < meta.last_page;

  const loadMore = () => {
    if (!hasMore || isFetching || !companyId) {
      return;
    }

    trigger({ companyId, page: meta.current_page + 1, pageSize: LOAD_MORE_PAGE_SIZE })
      .unwrap()
      .then((res) => {
        setItems((prev) => [...prev, ...(res.data ?? [])]);
        setMeta(res.meta ?? null);
      })
      .catch(() => null);
  };

  if (!isLoggedIn || !hasCashbackProgram || (!isFetching && items.length === 0)) {
    return null;
  }

  return (
    <section className="layout-container pb-lg md:pb-2xl">
      <div className="mb-md">
        <p className="text-[10px] uppercase tracking-[0.2em] text-brand-primary font-bold mb-2xs">
          {t('history.eyebrow')}
        </p>
        <h2 className="text-xl md:text-2xl font-bold text-brand-text-primary">{t('history.title')}</h2>
      </div>

      <div className="bg-brand-card rounded-xl border-[1.5px] border-brand-border overflow-hidden">
        <div className="divide-y divide-brand-border">
          {items.map((operation, index) => {
            const presentation = SOURCE_PRESENTATION[operation.source] ?? { icon: '💰', sign: 'positive' as const };
            const sign = presentation.sign === 'positive' ? '+' : '−';
            const colorClass =
              presentation.sign === 'positive'
                ? 'text-success'
                : presentation.sign === 'redeem'
                  ? 'text-brand-primary'
                  : 'text-danger';
            const dateLabel = moment(operation.date).format('MMM D');
            const orderDetail = operation.order
              ? t('history.detail_with_order', {
                  number: operation.order.number ?? '',
                  amount: formatCurrency(operation.order.total ?? 0),
                })
              : null;
            const detail = [dateLabel, orderDetail].filter(Boolean).join(' · ');

            return (
              <div key={`${operation.date}-${index}`} className="flex items-center justify-between px-md py-sm gap-sm">
                <div className="flex items-center gap-sm min-w-0">
                  <span className="w-7 h-7 rounded-full bg-muted flex items-center justify-center text-xs shrink-0">
                    {presentation.icon}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-brand-text-primary truncate">
                      {t(`history.sources.${operation.source}`)}
                    </p>
                    <p className="text-[11px] text-muted-foreground truncate">{detail}</p>
                  </div>
                </div>
                <span className={classnames('text-sm font-bold tabular-nums shrink-0', colorClass)}>
                  {sign}
                  {Math.abs(operation.value)} pts
                </span>
              </div>
            );
          })}

          {(hasMore || (isFetching && items.length === 0)) && (
            <button
              type="button"
              onClick={loadMore}
              disabled={isFetching || !hasMore}
              className="w-full px-md py-sm flex items-center justify-center gap-xs text-xs font-bold uppercase tracking-wider text-brand-primary hover:bg-muted/30 transition-colors disabled:opacity-60"
            >
              {isFetching ? <Loader2 className="w-4 h-4 animate-spin" /> : t('history.load_more')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
};

const rewardsPill =
  'inline-flex items-center gap-2xs bg-brand-primary/15 border-xs border-brand-primary/25 rounded-full px-md py-2xs text-xs font-semibold text-brand-primary uppercase tracking-wider';

const ctaButton =
  'bg-brand-primary text-brand-button-primary-text font-bold px-xl py-sm md:py-md rounded-full text-sm uppercase tracking-wide hover:opacity-90 transition-all inline-flex items-center gap-xs';

const lifetimeStatCard = 'bg-brand-card border border-brand-border rounded-xl px-sm py-xs text-center min-w-[120px]';

const stepCard =
  'relative bg-brand-card rounded-xl border-xs border-brand-border p-sm md:p-md text-center hover:border-brand-primary/30 transition-colors';

const stepBadge =
  'absolute -top-[10px] left-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-brand-primary text-brand-button-primary-text flex items-center justify-center text-[10px] font-bold';
